// packages/react/src/components/App.tsx
// App-shell entry for the IVW-2.0 app-shell/router (ported for IVW-1.0 app.js parity).

import React, { useEffect, useState } from 'react';
import type { FC } from 'react';
import { HashRouter } from 'react-router-dom';
import Theme from '@amzn/meridian/theme';
import blueDarkTokens from '@amzn/meridian-tokens/theme/blue-dark';
import blueLightTokens from '@amzn/meridian-tokens/theme/blue-light';
import '@amzn/meridian-tokens/base/font/amazon-ember.css';
import './global.css';
import { DeviceLayoutProvider } from './common/context/DeviceLayoutContext';
import AppRoutes from './AppRoutes';
import RouteMetrics from './RouteMetrics';
import { DarkMode } from './util/DarkMode';
import { getDarkModeStatus, subscribeDarkMode } from './handler/ParentDataHandler';

/**
 * App — the IVW-2.0 app-shell/router (v1 app.js AppComponent + App lifecycle parity).
 *
 * Self-contained: renders its OWN `HashRouter`, so it is a drop-in entry component for the
 * embedding host (no Router ancestor required). Hash routing matches how the SCC shell drives
 * the embedded view (e.g. `#/associate`), mirroring the working v1 app. Composition nesting is:
 *   DeviceLayoutProvider > HashRouter > (RouteMetrics + Meridian Theme > AppRoutes)
 * `RouteMetrics` (renders null) is a Router sibling that precedes the `<Theme>` so it owns the
 * `history.listen` + route-path metric emission alongside the themed routed content.
 *
 * Dark mode: state defaults to `DarkMode.LIGHT`; on mount it subscribes to host dark-mode updates
 * and requests the current status. The Meridian `<Theme>` receives `mode='dark'` iff the state is
 * `DarkMode.DARK`, else `mode='light'` (behaviorally equivalent to v1's blueDark/blueLight
 * selection under `@amzn/meridian-tokens@8.13.1`). The subscribe unsubscribe fn is returned from
 * the effect so the listener is removed on unmount (no leak).
 */
const App: FC = () => {
  const [darkMode, setDarkMode] = useState<DarkMode>(DarkMode.LIGHT);

  useEffect(() => {
    const unsubscribe = subscribeDarkMode(setDarkMode);
    getDarkModeStatus();
    return unsubscribe;
  }, []);

  return (
    <DeviceLayoutProvider>
      <HashRouter>
        <RouteMetrics />
        <Theme tokens={darkMode === DarkMode.DARK ? blueDarkTokens : blueLightTokens}>
          <AppRoutes />
        </Theme>
      </HashRouter>
    </DeviceLayoutProvider>
  );
};

export default App;
